import React from "react";
import { Check, Loader2 } from "lucide-react";
// The design's own artwork. The three chips carry their tinted background with
// them, which is why nothing here draws one around them.
import { ReactComponent as CarriesBrandMark } from "../../../assets/svg/form/form-1.svg";
import { ReactComponent as WorksEverywhereMark } from "../../../assets/svg/form/form-2.svg";
import { ReactComponent as OneLinkMark } from "../../../assets/svg/form/form-3.svg";
import { ReactComponent as CopyLinkIcon } from "../../../assets/svg/form/copy_link.svg";

/**
 * The card that names the link and hands it over.
 *
 * One field, and it is the whole card: what a practitioner types here becomes
 * a permanent address that goes in an Instagram bio and into a hundred WhatsApp
 * messages, so it is worth a screen rather than a row on the details form.
 *
 * The box draws the address, with only the name in it editable. A plain field
 * asking for "your link name" leaves the person guessing what they are naming;
 * a field with `.naurist.com` standing at the end of it says what is being
 * named without a word of help text — and that piece comes off the same
 * template the copied link is built from, so the picture cannot disagree with
 * the link.
 */

/**
 * The address as the box shows it: no scheme, no trailing slash.
 *
 * `https://` and the slash after the domain are true of the link and say
 * nothing to the person naming it — they are punctuation, and in a box this
 * narrow they crowd out the only part that is theirs to decide. What is left
 * still comes off the same template, so the box and the copied link cannot
 * drift apart; it is only the parts nobody types that are dropped.
 *
 * The prefix is not always a scheme alone. An older shared-host link puts the
 * name in the path — `connect.naurist.com/rohan` — and what survives the strip
 * there is the host, which still belongs in front of the name.
 */
const withoutScheme = (prefix: string) => prefix.replace(/^[a-z]+:\/\//i, "");
const withoutTrailingSlash = (suffix: string) => suffix.replace(/\/+$/, "");

/** What stands in the box before anything is typed — the design's own. */
const PLACEHOLDER = "yourname";

/**
 * The field's own box.
 *
 * Written here rather than taken from `controlBoxClasses` because the design
 * gives this one control a fill of its own — a grey field on a white card,
 * where every other control in the app is white. The geometry is the shared
 * shell's to the pixel, so it still stands at the height a form row does; only
 * the two colours differ.
 *
 * The border still turns red on a rejected name. That is the one thing the box
 * has to be able to say for itself, and the line under it is not always in the
 * reader's eye when the answer arrives.
 */
const boxClasses = (invalid: boolean, disabled: boolean) =>
  "flex w-full items-center gap-1.5 rounded-[6px] border px-3.5 py-2.5 3xl:py-3 " +
  "bg-[#F9F9F9] text-[14px] 3xl:text-[15px] transition-colors " +
  "focus-within:outline-none " +
  (disabled ? "cursor-not-allowed text-ink-muted " : "text-ink ") +
  (invalid
    ? "border-red-400 focus-within:border-red-500"
    : "border-[#E0E0E0] focus-within:border-brand");

/**
 * The button in the slot beside the field — the same shell whichever of the
 * two is showing, so the swap between them is a fade and not a resize.
 *
 * Absolute, because they are stacked: both are mounted at once and the one
 * that is not the current job is faded out under the other.
 */
const actionClasses =
  "absolute inset-0 flex items-center justify-center rounded-[6px] " +
  "bg-[#007E8C] text-white hover:bg-[#006A76] " +
  "transition-[opacity,background-color] duration-300 ease-out " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007E8C]/40 " +
  "disabled:cursor-not-allowed disabled:bg-[#D7F9FD] disabled:text-[#00899B]";

type Mark = React.FC<React.SVGProps<SVGSVGElement>>;

const BENEFITS: Array<{
  Mark: Mark;
  title: string;
  body: string;
}> = [
  {
    Mark: CarriesBrandMark,
    title: "Carries your brand",
    body: "Your brand greets every client who opens the form.",
  },
  {
    Mark: WorksEverywhereMark,
    title: "Instagram & WhatsApp friendly",
    body: "Works smoothly on mobile, desktop, and Instagram's in-app browser.",
  },
  {
    Mark: OneLinkMark,
    title: "One link to share",
    body: "Drop it in your bio, WhatsApp, or email.",
  },
];

export interface FormLinkCardProps {
  /** The name as typed. The card never normalises it — see `onNameChange`. */
  name: string;
  /**
   * Given the raw keystroke value. The container is what runs it through
   * `normaliseLinkName`, because the same corrected string is what feeds the
   * address shown in the preview beside this card.
   */
  onNameChange: (value: string) => void;
  onBlur: () => void;
  /** Fixed text either side of the name, from `formLinkAffixes`. */
  prefix: string;
  suffix: string;
  /** Printed under the field. Whether it is the app's objection or the
   *  server's is not a difference worth showing the person reading it. */
  error?: string;
  /**
   * The other answer to the same question: this name is free. Drawn in the
   * same line as `error`, which wins where both are somehow set — a name that
   * cannot be claimed is the more urgent of the two things to say.
   */
  available?: string;
  /**
   * The whole address, scheme and trailing slash included — what the box shows
   * once the name is claimed. The backend's own `share_url`, never assembled
   * here.
   */
  url: string;
  /**
   * True once the name is claimed and the subdomain registered with it.
   *
   * It is the whole difference between the two states of this card: the box
   * stops taking edits and shows the finished address, and the button stops
   * being Save and becomes Copy. Nothing else on the card moves, which is what
   * lets the swap be a fade rather than a redraw.
   */
  locked: boolean;
  /**
   * True while the name is being claimed or its subdomain registered — the two
   * calls Save makes, and the only two this card ever waits on.
   *
   * Deliberately not true while the name is being checked. That question is
   * asked on its own timer as the letters go in, and a button that greyed out
   * and span for each of them would flicker through every name on the way to
   * the one being typed. The check reports under the field; the button waits
   * for nothing but the press.
   */
  saving: boolean;
  /** Whether the name in the box is one Save may be pressed for. */
  canSave: boolean;
  /** True for the moment after a successful copy. */
  copied: boolean;
  /** Claims the name and registers the subdomain — both, in that order. */
  onSave: () => void;
  onCopy: () => void;
  /**
   * A copy that was taken out of the field by hand rather than pressed for.
   * Called so the screen can say what to do instead - a copy that silently
   * does nothing reads as a broken keyboard.
   */
  onCopyBlocked: () => void;
}

const FormLinkCard: React.FC<FormLinkCardProps> = ({
  name,
  onNameChange,
  onBlur,
  prefix,
  suffix,
  error,
  available,
  url,
  locked,
  saving,
  canSave,
  copied,
  onSave,
  onCopy,
  onCopyBlocked,
}) => {
  const errorId = "form-link-error";

  const shownPrefix = withoutScheme(prefix);
  const shownSuffix = withoutTrailingSlash(suffix);

  /**
   * Before it is claimed, the address does not leave this card at all.
   *
   * Selecting the field and pressing Ctrl-C copies the name on its own -
   * `diksha-kumari`, without the `https://` before it or the domain after -
   * which is not a link, and pasting it into a WhatsApp message sends a client
   * a word. Worse, the name in the box is not claimed yet: Save is what claims
   * it, so a hand-copied address can be one that answers for nobody, or for
   * somebody else.
   *
   * Cut and drag are the same act by another route, so all three are refused
   * together. Selecting and typing over it is untouched - this stops the
   * address leaving, not the name being edited.
   *
   * Once the box is locked none of this applies: what stands in it then is the
   * finished address, claimed and served, and taking it by hand hands over
   * exactly what the Copy button would.
   */
  const refuse = (event: React.SyntheticEvent) => {
    if (locked) return;
    event.preventDefault();
    onCopyBlocked();
  };

  /** Save is busy for both of its calls, and for nothing else. */
  const busy = saving;

  return (
    <div className="rounded-md border border-line bg-white p-5 sm:p-6 3xl:p-8">
      <h3 className="text-[22px] 3xl:text-[26px] font-semibold text-[#00424B]">
        Your personalized form link
      </h3>
      <p className="mt-2 max-w-[560px] text-[14px] 3xl:text-[15px] leading-6 text-ink-muted">
        A custom link for your enquiry forms. Share it with clients to simplify
        your workflow.
      </p>

      {/* `items-stretch`, so the button is exactly as tall as the field beside
          it at every breakpoint rather than at the one it was measured on.

          Capped rather than run to the card's edge: the box holds one short
          word, and a field three times longer than anything that can go in it
          reads as an address bar to paste into rather than as a name to
          choose. */}
      <div className="mt-5 flex max-w-[400px] items-stretch gap-3 3xl:max-w-[460px]">
        <div className={`${boxClasses(!!error, saving)} min-w-0 flex-1`}>
          {/* One row inside the box: the name at the left where the cursor
              lands, the domain held against the right edge. The box's own
              `gap` never applies, because this is its only child. */}
          <span className="flex w-full min-w-0 items-center">
            {/* `select-none` on the fixed halves: they are a picture of the
                address rather than text to take away, and a drag across the
                box that picked them up would hand over a broken half-link.
                Empty on a subdomain link, where the scheme was the whole of
                what stood in front of the name — see `withoutScheme`. */}
            {!locked && shownPrefix && (
              <span className="flex-shrink-0 select-none text-ink-subtle">
                {shownPrefix}
              </span>
            )}

            {/* The name takes the room that is left, which is what pushes the
                domain to the far end of the field. Sized by the box rather
                than by its own contents: the address is read as a name and a
                place, and the two belong at the two ends of the box. */}
            <input
              id="form-link-name"
              // What the box is showing is a different thing in the two
              // states, so it is named as one: a name to choose while it is
              // being chosen, the link itself once it is settled.
              aria-label={locked ? "Your form link" : "Your form link name"}
              aria-invalid={!!error}
              aria-describedby={error || available ? errorId : undefined}
              // The whole address once it is claimed - scheme and trailing
              // slash included, which the two halves either side are dropped
              // for. There is nothing left to choose in it, so nothing is left
              // out of it either: what stands in the box is what a client will
              // be sent, character for character.
              value={locked ? url : name}
              onChange={(event) => onNameChange(event.target.value)}
              onBlur={onBlur}
              onCopy={refuse}
              onCut={refuse}
              onDragStart={refuse}
              // `readOnly` rather than `disabled` for a claimed link: the text
              // still selects and still reads at full contrast, which a greyed
              // field would not, and it is an address to take away rather than
              // a control that has been switched off.
              readOnly={locked}
              disabled={saving && !locked}
              placeholder={PLACEHOLDER}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              // `size={1}` so the input asks for almost no width of its own:
              // an input's default intrinsic size is around twenty characters,
              // and a flex item is not allowed to shrink below that.
              size={1}
              className={
                "min-w-0 flex-1 bg-transparent p-0 font-medium " +
                // The name is the one piece of the address that is theirs, so
                // it is set in the brand colour once it is typed. Grey until
                // then, with the prefix and the domain either side of it: a
                // stand-in in the brand colour reads as a name already
                // claimed, which is the one thing this box must not say.
                "text-brand-dark placeholder:font-medium " +
                "placeholder:text-ink-subtle " +
                "focus:outline-none disabled:cursor-not-allowed"
              }
            />

            {!locked && (
              <span className="flex-shrink-0 select-none pl-2 text-ink-subtle">
                {shownSuffix}
              </span>
            )}
          </span>
        </div>

        {/*
          Two buttons in one slot, and only ever one of them to press.

          Save is the whole of the commitment: it claims the slug and registers
          the subdomain, and until both are stored there is no address worth
          copying — a Copy on this card before that would hand somebody a link
          that answers for nobody. Once they are, there is nothing left to save
          and the only thing left to do with the address is take it away, which
          Copy does without touching the server at all.

          They swap by fading through each other in a slot that resizes rather
          than by one unmounting and the other appearing: the card does not
          jump, and the change reads as the same control finishing its job.
          Both are mounted throughout, so the one that is not the current job
          is disabled — which keeps it out of the tab order and out of the
          accessibility tree along with `aria-hidden`.

          Hand-rolled rather than a `Button`: these are sized to the field
          beside them, and the size variants all carry padding of their own.
        */}
        <div
          className={
            "relative flex-shrink-0 transition-[width] duration-300 ease-out " +
            (locked ? "w-[46px] 3xl:w-[52px]" : "w-[96px] 3xl:w-[108px]")
          }
        >
          {/* The deeper teal rather than the app's primary fill - the same one
              `Button`'s `plan` variant wears, and darkened by the same step on
              hover. It sits beside a long pale field carrying the one action on
              the card, and the lighter fill reads as decoration next to it. */}
          <button
            type="button"
            onClick={onSave}
            disabled={locked || busy || !canSave}
            aria-hidden={locked}
            className={
              actionClasses +
              (locked ? " pointer-events-none opacity-0" : " opacity-100")
            }
          >
            <span className="inline-flex items-center gap-2 text-[14px] 3xl:text-[15px] font-medium">
              {busy && (
                <Loader2 size={16} className="animate-spin" aria-hidden="true" />
              )}
              Save
            </span>
          </button>

          <button
            type="button"
            onClick={onCopy}
            disabled={!locked}
            aria-hidden={!locked}
            aria-label={copied ? "Link copied" : "Copy your link"}
            title={copied ? "Link copied" : "Copy your link"}
            className={
              actionClasses +
              (locked ? " opacity-100" : " pointer-events-none opacity-0")
            }
          >
            {copied ? (
              <Check size={20} aria-hidden="true" />
            ) : (
              // The artwork paints its strokes white, which is right on the
              // filled button and invisible on the pale disabled one - so the
              // colour is taken back off the button here. A CSS rule outranks
              // the presentation attribute in the file, which leaves the asset
              // itself untouched.
              <CopyLinkIcon
                aria-hidden="true"
                className="h-5 w-5 [&_path]:stroke-current"
              />
            )}
          </button>
        </div>
      </div>

      {/*
        Whether the name can be had, in one line under the field it is about
        rather than in a banner over the card: there is one field here, and a
        banner would say less about it.

        The line's space is held whether or not there is anything in it. The
        check lands a moment after the typing stops — a message that appeared
        into the layout would push the list below it down under the reader's
        eye, and the answer they were waiting for would arrive as a jump.

        `role="alert"` so the reason a press did nothing is announced.
      */}
      <p
        id={errorId}
        role="alert"
        className={
          "mt-2 min-h-[20px] text-[13px] leading-5 " +
          (error ? "text-red-500" : "text-[#2F8E5B]")
        }
      >
        {error || available}
      </p>

      {/* What the link is for, named before the three answers to it — the list
          underneath reads as a set of claims about nothing until something
          says what they are about. */}
      <h4 className="mt-6 text-[14px] 3xl:text-[15px] text-ink-muted 3xl:mt-8">
        Why share your link
      </h4>

      <ul className="mt-4 space-y-4">
        {BENEFITS.map(({ Mark, title, body }) => (
          <li key={title} className="flex items-start gap-3">
            <Mark
              aria-hidden="true"
              className="h-[34px] w-[34px] flex-shrink-0"
            />
            <div className="min-w-0">
              <p className="text-[14px] 3xl:text-[15px] font-medium text-ink">
                {title}
              </p>
              <p className="mt-0.5 text-[13px] 3xl:text-[14px] leading-5 text-ink-muted">
                {body}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default FormLinkCard;
